import {
  Fragment,
  useRef,
  useEffect,
  useCallback,
  MouseEvent,
  MouseEventHandler,
} from "react";
import ExtractedCell from "../models/ExtractedCell";
import { ExtractedRow } from "../models/ExtractedRow";
import { HighlightColours } from "../models/HighlightColors";
import "../styles/extractedTableViewer.css";
import { ColumnConfig } from "../models/config/ColumnConfig";
import { TableConfig } from "../models/config/TableConfig";
import { ExtractedTableData } from "../models/extraction/ExtractedTableData";
import "../styles/extractedTableViewer.css";
import { isBodyRow } from "../types/RowType";
import { useHighlightDispatch } from "./highlightProvider/HighlightProvider";
import { useAsync } from "./hooks/useAsync";
import { useIsInitialRender } from "./hooks/useIsInitialRender";
import { usePageDispatch, usePageState } from "./pageProvider/PageProvider";
import { AdvancedTooltip } from "./tooltips/AdvancedTooltip";
import type { Page, PageSpans } from "../models/Page";
import type Span from "../models/Span";
import type { BoxHighlightObject } from "../models/HighlightObject";
import { HighlightObjectFactory } from "../models/HighlightObjectFactory";

export { ExtractedTableViewer };

type Props = {
  tableConfig: TableConfig;
  extractedTable?: ExtractedTableData;
  pageIdToIndex: (pageId: string) => number;
  highlightPageRow: (
    pageId: string,
    pageRowNumber: number,
  ) => Promise<[Page, Span[]]>;
  getPageById: (pageId: string) => Promise<PageSpans>;
};

/**
 * Generate error message from cell, includes Transformation, Validation, OCR Warnings
 */
function cellErrorMessage(
  cell: ExtractedCell,
  columnConfig: ColumnConfig,
): string | undefined {
  let message = "";
  if (!cell.valid) {
    message += `Cell is not valid\n`;
  }
  if (cell.transformed) {
    message += `Cell was transformed\n`;
  }
  if (cell.hasOcrWarning) {
    message += `Cell OCR confidence ${cell.ocrConfidence}% below threshold\n`;
  }
  return message !== "" ? message : undefined;
}

const ExtractedTableViewer = (props: Props) => {
  const extractedTable = props.extractedTable
    ? props.extractedTable.debug_GetExtracted()
    : undefined;
  const visibleColumns = [
    "ROW_NUMBER",
    ...props.tableConfig.columnNames
      .filter((x) => !x.actualHidden)
      .map((x) => x.fieldName),
  ];
  const columnNum = visibleColumns.length;
  // filter ExtractedCell by body rows
  const sortedBodyRows = (extractedTable?.rows ?? [])
    .filter((x) => isBodyRow(x));

  // group rows by page index
  const groupedRows = sortedBodyRows.reduce(
    (prev: { [pageIndex in string]: ExtractedRow[] }, curr: ExtractedRow) => {
      if (prev[curr.pageId + ""] == undefined) {
        prev[curr.pageId + ""] = [];
      }
      prev[curr.pageId].push(curr);
      return prev;
    },
    {} as { [pageIndex in string]: ExtractedRow[] },
  );

  return (
    <div className={"extractedTableWrapper"}>
      <table className={"extractedTable"}>
        <colgroup>
          <col style={{ width: "10ch" }} />
          <col style={{ width: "auto" }} />
        </colgroup>
        <thead>
          <tr>
            {visibleColumns.map((columnName, i) => {
              const displayHeader =
                columnName.toUpperCase() === "ROW_NUMBER" ? "Row #" : columnName;
              return (
                <th
                  key={`${columnName}`}
                  style={{
                    backgroundColor: HighlightColours.getHighlightColor(i),
                  }}
                >
                  {displayHeader}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Object.keys(groupedRows).map((x, groupIndex) => {
            const rows = groupedRows[x];
            const pageIndex = props.pageIdToIndex(x);

            return (
              <Fragment key={groupIndex}>
                {pageIndex !== -1 && (
                  <tr className={"pageBreak"}>
                    <td colSpan={1}>Page {pageIndex + 1}</td>
                  </tr>
                )}
                {rows.map((row, rowIndex) => (
                  <Row
                    key={`${groupIndex}-${rowIndex}`}
                    row={row}
                    getPage={props.getPageById}
                    pageIndex={pageIndex}
                    groupIndex={groupIndex}
                    rowIndex={rowIndex}
                    highlightPageRow={props.highlightPageRow}
                    tableConfig={props.tableConfig}
                    visibleColumns={visibleColumns}
                  />
                ))}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            {sortedBodyRows.length === 0 && (
              <td colSpan={columnNum}>No Rows Extracted</td>
            )}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

type RowProps = {
  groupIndex: number;
  pageIndex: number;
  rowIndex: number;
  highlightPageRow: Props["highlightPageRow"];
  getPage: Props["getPageById"];
  row: ExtractedRow;
  tableConfig: TableConfig;
  visibleColumns: string[];
};
const Row = (props: RowProps) => {
  const { groupIndex, rowIndex, row, visibleColumns } = { ...props };
  const isInitial = useIsInitialRender();
  const highDispatch = useHighlightDispatch();
  const pageState = usePageState();
  const pageDispatch = usePageDispatch();

  const highlightRef = useRef<BoxHighlightObject | undefined>(undefined);
  const higlightColorRef = useRef(
    HighlightColours.getHighlightColor(rowIndex),
  ).current;

  const [result, trigger, loading] = useAsync(async (arg) => {
    try {
      return await props.getPage(arg);
    } catch (e) {
      return Promise.resolve({
        height: 0,
        pageId: arg,
        spans: [],
        width: 0,
      } as PageSpans);
    }
  }, undefined);

  const higlightRowOnMouseOver: MouseEventHandler<HTMLTableCellElement> =
    useCallback(
      (e: MouseEvent<HTMLTableCellElement>) => {
        // current row higlighted
        if (loading) return;
        console.log(
          `Highlighting row ${row.pageId}:${row.rowNumber}:${row.pageRowNumber}`,
          e,
        );
        // create Highlight Ref lazily
        if (highlightRef.current == undefined) {
          const rowSpanIds = row.cells.flatMap((x) => x.spanIds);
          const rowSpans = result!.spans.filter((span) =>
            rowSpanIds.includes(span.id),
          );
          highlightRef.current = HighlightObjectFactory.createRowHighlight(
            "ROW_HIGHLIGHT",
            higlightColorRef,
            rowSpans,
            result!.height,
            true,
          );
        }
        // make highlight visible
        highlightRef.current.visible = true;
        highDispatch({
          highlight: highlightRef.current,
          type: "add",
        });
        if (pageState.pageIndex !== props.pageIndex) {
          pageDispatch({
            pageIndex: props.pageIndex,
          });
        }
      },
      [result, pageState],
    );

  const unhighlightRowOnMouseOut: MouseEventHandler<HTMLTableCellElement> =
    useCallback(
      (e) => {
        // If highlight ref undefined, no highlight can be on page
        if (highlightRef.current == undefined) {
          return;
        }
        // make highlight invisble
        highlightRef.current.visible = false;
        highDispatch({
          highlight: highlightRef.current,
          type: "remove",
        });
      },
      [highDispatch, highlightRef],
    );

  useEffect(() => {
    if (isInitial) {
      trigger(props.row.pageId);
    }
  });

  return (
    <tr key={`${groupIndex}-${rowIndex}`}>
      {/* Display visual cells */}
      {result != undefined &&
        visibleColumns.map((columnName, ci) => {
          if (columnName.toUpperCase() === "ROW_NUMBER") {
            return (
              <td
                key={`${columnName}-${props.rowIndex}`}
                style={{ backgroundColor: higlightColorRef }}
                onMouseOver={higlightRowOnMouseOver}
                onMouseOut={unhighlightRowOnMouseOut}
              >
                {rowIndex + 1}
              </td>
            );
          }
          const c = row.cells.find((x) => x.fieldName === columnName);
          if (c) {
            return (
              <Cell
                key={`${c.fieldName}-${props.rowIndex}`}
                cell={c}
                cellIndex={ci}
                rowIndex={rowIndex}
                tableConfig={props.tableConfig}
              />
            );
          } else {
            return <td key={`${columnName}-${props.rowIndex}`} className="monospace" />;
          }
        })}
    </tr>
  );
};

type CellProps = {
  tableConfig: TableConfig;
  cell: ExtractedCell;
  cellIndex: number;
  rowIndex: number;
};

function Cell(props: CellProps) {
  const columnConfig = props.tableConfig.getDocFieldConfig(
    props.cell.fieldName,
  )!;
  const message = cellErrorMessage(props.cell, columnConfig);

  const className = `monospace ${columnConfig.multiline ? "multiline" : ""} ${message ? "error" : ""} ${!props.cell.valid ? "invalid" : ""} ${props.cell.transformed ? "transformed" : ""}`;

  if (message) {
    return (
      <AdvancedTooltip
        overflow
        short
        possiblePlacements={["bottom-center"]}
        tooltipText={() => message}
      >
        {(tProps) => (
          <td {...tProps} className={className}>
            {props.cell.fieldValue}
          </td>
        )}
      </AdvancedTooltip>
    );
  }
  return <td className={className}>{props.cell.fieldValue}</td>;
}
